import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from '../Axiosapi';
import envConstants from '../Constants';
import Tabs from "../Components/SingleProductDescription";
import ProductDetail from "../Components/SingleProductDetail";
import ProductImage from "../Components/SingleProductSlider";
import Reviews from "../Components/ProductReview";
import ProductSlider from "../Components/Sub-Components/ProductSlider";
import { useCart } from '../Context/CartContext'; // Import useCart hook
import { Helmet } from "react-helmet-async";

const ProductPageDetail = () => {
    const { sku } = useParams();
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [categories, setCategories] = useState([]);
    const [latestProducts, setLatestProducts] = useState([]);
    const { dispatch } = useCart();
    const badrequest = '/assets/images/bad-request.webp';
    const datanotfound = '/assets/images/data-not-found.webp';

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await axios.get(`${envConstants.API_BASE_URL}/categories`);
                setCategories(response.data.data); // Update to use response.data.data
            } catch (error) {
                setError(error);
                console.error(error);
            } finally {
                setLoading(false);
            }
        };

        const fetchLatestProducts = async ({ pagination = 20, json = false } = {}) => {
            try {
                const response = await axios.get(`${envConstants.API_BASE_URL}/latest-products`, {
                    params: { pagination },
                    responseType: json ? 'json' : 'json',
                });
                setLatestProducts(response.data.data.data); // Correctly accessing the nested data array
            } catch (error) {
                setError(error);
                console.error(error);
            } finally {
                setLoading(false);
            }
        };

        fetchCategories();
        fetchLatestProducts();

    }, []);

    useEffect(() => {
        const fetchProduct = async () => {
            try {
                const encodedSku = encodeURIComponent(sku);
                const response = await axios.get(`${envConstants.API_BASE_URL}/products/${encodedSku}`);
                setProduct(response.data.data);
            } catch (error) {
                setError(error);
                console.error('Error fetching product:', error.response ? error.response.data : error.message);
            } finally {
                setLoading(false);
            }
        };

        fetchProduct();
    }, [sku]);


    if (loading) return <div>Loading...</div>;

    if (error) {
        const isNotFound = error.response && error.response.status === 404;
        return (
            <div className='text-center'>
                <img
                    src={isNotFound ? datanotfound : badrequest}
                    style={{ width: "40%", margin: "20px 0px" }}
                    alt={isNotFound ? "Not Found" : "Bad Request"}
                />
            </div>
        );
    }

    const schemaMarkup = {
        "@context": "https://schema.org",
        "@type": "Product",
        "name": product?.product_meta?.find(meta => meta.option === '_seo_title')?.value || product?.title || '',
        "image": `${envConstants.API_BASE_PUBLIC_URL}${product?.featured_image}`,
        "description": product?.short_description,
        "sku": product?.sku || '',
        "brand": {
            "@type": "Brand",
            "name": product?.brand?.title
        },
        "url": `${envConstants.FRONTEND_URL}product/${encodeURIComponent(product?.permalink)}`,
        "offers": {
            "@type": "Offer",
            "priceCurrency": "AUD",
            "price": product?.product_meta?.find(meta => meta.option === '_price')?.value || '',
            "availability": "https://schema.org/InStock",
            "itemCondition": "https://schema.org/NewCondition",
            "url": `${envConstants.FRONTEND_URL}product/${encodeURIComponent(product?.permalink)}`,
            "shippingDetails": {
                "@type": "OfferShippingDetails",
                "shippingSettingsLink": "https://autopartstation.com.au/shipping-policy"
            },
            "hasMerchantReturnPolicy": {
                "@type": "MerchantReturnPolicy",
                "url": "https://autopartstation.com.au/return-policy",
                "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
                "merchantReturnDays": 30,
                "returnMethod": "https://schema.org/ReturnByMail",
                "returnFees": "https://schema.org/FreeReturn"
            }
        }
    };
    const index = product?.product_meta?.find(meta => meta.option === '_page_index')?.value || '1';


    return (
        <div className="container p-sm-2">
            <Helmet>
                <title>
                    {`${product && (product?.product_meta?.find(meta => meta.option === '_seo_title')?.value || product?.title) || ''}`}
                </title>
                <meta
                    name="description"
                    content={product && (product?.product_meta?.find(meta => meta.option === '_seo_description')?.value || product?.title) || ''}
                />
                <meta
                    name="author"
                    content={envConstants.STORE_META_AUTHOR ? envConstants.STORE_META_AUTHOR : 'Auto Part Station Store'}
                />
                <meta name="robots" content={index == 0 || index == '0' ? 'noindex, nofollow' : 'index, follow'} />
                <meta
                    name="keywords"
                    content={product && (product?.product_meta?.find(meta => meta.option === '_seo_tags')?.value || envConstants.STORE_META_KEYWORDS) || ''}
                />
                <link rel="canonical" href={product?.product_meta?.find(meta => meta.option === '_canonical_url')?.value || `${envConstants.FRONTEND_URL}product/${encodeURIComponent(product?.permalink)}`} />
                <script type="application/ld+json">
                    {JSON.stringify(product?.product_meta?.find(meta => meta.option === '_schema_markup')?.value || schemaMarkup)}
                </script>
            </Helmet>


            <div className="d-flex mt-3 mb-3" style={{ width: '100%' }}>
                <div className="row" style={{ width: '100%', margin: 'auto' }}>
                    <div className="col-12 col-md-5 mb-3 mb-md-0 product-detail-container">
                        <ProductImage
                            galleryMedia={product && product.gallery_media}
                            featuredImage={product && product.featured_image}
                        />
                    </div>
                    <div className="col-12 col-md-7 product-detail-box">
                        <ProductDetail product={product} dispatch={dispatch} />
                    </div>
                </div>
            </div>
            <div>
                <Tabs product={product} /> {/* Pass product prop */}
            </div>
            <div className="porduct-section">
                <h2 className="heading-2">New Products</h2>
                <p className="theme-text">Check out our collection of the top New Products that encourage conversion.</p>
                <ProductSlider products={latestProducts} categories={categories} badge="New" />
            </div>
            <div>
                {/*  <Reviews />*/}
            </div>
        </div>
    );
};

export default ProductPageDetail;